import { Ionicons } from '@expo/vector-icons';
import React from 'react';
import {
  ActivityIndicator,
  FlatList,
  Image,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { Team } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { PatternBackground } from '@/src/presentation/components';
import { createStyles } from './ExploreTeamsScreen.styles';

interface ExploreTeamsScreenProps {
  teams: Team[];
  searchQuery: string;
  loading: boolean;
  loadingMore: boolean;
  onSearchChange: (value: string) => void;
  onTeamPress: (teamId: string) => void;
  onEndReached: () => void;
  colors: ThemeColors;
}

export const ExploreTeamsScreen: React.FC<ExploreTeamsScreenProps> = ({
  teams,
  searchQuery,
  loading,
  loadingMore,
  onSearchChange,
  onTeamPress,
  onEndReached,
  colors,
}) => {
  const { t } = useTranslation();
  const styles = createStyles();

  const filtered = searchQuery
    ? teams.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : teams;

  return (
    <PatternBackground>
      <View style={[styles.header, { backgroundColor: colors.surface, borderBottomColor: colors.border }]}>
        <View style={[styles.searchContainer, { backgroundColor: colors.background, borderColor: colors.border }]}>
          <Ionicons name="search" size={20} color={colors.textTertiary} style={{ marginRight: 8 }} />
          <TextInput
            style={[styles.searchInput, { color: colors.text }]}
            placeholder={t('teams.discoverTeams')}
            placeholderTextColor={colors.textTertiary}
            value={searchQuery}
            onChangeText={onSearchChange}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => onSearchChange('')}>
              <Ionicons name="close-circle" size={20} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item) => item.id}
          contentContainerStyle={filtered.length === 0 ? styles.emptyList : styles.listContent}
          onEndReached={onEndReached}
          onEndReachedThreshold={0.3}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}
              onPress={() => onTeamPress(item.id)}
              activeOpacity={0.7}
            >
              <View style={styles.cardHeader}>
                <View style={[styles.logoCircle, { borderColor: colors.border, backgroundColor: colors.background }]}>
                  {item.logoUrl ? (
                    <Image source={{ uri: item.logoUrl }} style={styles.logoImage} />
                  ) : (
                    <Ionicons name="people" size={18} color={colors.textTertiary} />
                  )}
                </View>
                <Text style={[styles.cardName, { color: colors.text }]} numberOfLines={1}>{item.name}</Text>
                <Ionicons name="chevron-forward" size={16} color={colors.textTertiary} />
              </View>
              {item.description ? (
                <Text style={[styles.cardDescription, { color: colors.textSecondary }]} numberOfLines={2}>
                  {item.description}
                </Text>
              ) : null}
              <View style={styles.cardFooter}>
                <Ionicons name="people-outline" size={14} color={colors.textTertiary} />
                <Text style={[styles.cardFooterText, { color: colors.textTertiary }]}>
                  {item.isPublic ? t('teams.public') : t('teams.private')}
                </Text>
              </View>
            </TouchableOpacity>
          )}
          ListEmptyComponent={
            <View style={styles.empty}>
              <Ionicons name="people-outline" size={52} color={colors.textTertiary} style={{ marginBottom: 16 }} />
              <Text style={[styles.emptyText, { color: colors.textSecondary }]}>{t('teams.noPublicTeams')}</Text>
              <Text style={[styles.emptySub, { color: colors.textTertiary }]}>{t('teams.noPublicTeamsDesc')}</Text>
            </View>
          }
          ListFooterComponent={
            loadingMore ? (
              <View style={styles.footer}>
                <ActivityIndicator color={colors.primary} />
              </View>
            ) : null
          }
        />
      )}
    </PatternBackground>
  );
};
